// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ChevronLeft from "@synd/XDL/Icons/PaginationLeftArrow";
import ChevronRight from "@synd/XDL/Icons/PaginationRightArrow";
import Close from "@synd/XDL/Icons/Close";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import { color, createLinkIconStyles, getSystemColor, xScreen } from "@synd/XDL/Utilities";
import { ButtonIcon } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import PanelMenuDefaultProps from "@insite/mobius/PanelMenu/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";
import { LazyImageStyle } from "@insite/mobius/LazyImage";

export default {
    defaultProps: {
        ...PanelMenuDefaultProps,
        bodyColor: "common.ui1",
        headerColor: "common.ui1",
        cssOverrides: {
            menu: css`
                padding: 0 0 16px 0;
            `,
            children: css`
                background-color: ${getSystemColor("common.ui1")};
                z-index: 1;
            `,
            wrapper: css`
                ${StyledButton} {
                    padding: 8px 16px;
                    text-align: left;
                    width: 100%;

                    ${IconWrapper} {
                        color: ${getSystemColor("common.ui7")};
                    }
                    &:hover,
                    &:focus {
                        ${IconWrapper} {
                            color: ${getSystemColor("common.ui9")};
                        }
                        ${TypographyStyle} {
                            color: ${getSystemColor("common.ui9")};
                            border-bottom: 2px solid ${getSystemColor("common.ui9")};
                            width:fit-content;
                        }
                    }
                }
            `,
        },
        childTypographyProps: {
            size: fontConfig.smallSingleLine.size,
            lineHeight: fontConfig.smallSingleLine.lineHeight,
            css: css`
                display: block;
                word-wrap: break-word;
                width: 100%;
            `,
        },
        backIconProps: {
            src: ChevronLeft,
            width: 10,
            height: 16,
            css: css`
                &&& {
                    color: ${getSystemColor("primary")};
                }
            `,
        },
        closeIconProps: {
            css: css`
                display: none;
            `,
        },
        panelRowProps: {
            color: "common.ui1",
            moreIconProps: {
                src: ChevronRight,
                size: 9,
                css: css`
                    color: ${getSystemColor("common.ui9")};
                    justify-content: flex-end;
                `,
            },
            css: css`
                padding: 8px 16px;
                color: ${getSystemColor("common.ui7")};
                ${({ header }: any) => {
                    if (header) {
                        return css`
                            border-bottom: 1px solid ${getSystemColor("common.border")};
                            padding: 16px 0;
                            margin: 0 16px 8px;
                            button {
                                font-size: 14px;
                                line-height: 19px;
                                display: flex;
                                align-items: center;
                                gap: 12px;
                                ${ButtonIcon} {
                                    top: 0;
                                }
                            }
                        `;
                    }
                }}

                ${({ isCurrent }: any) => {
                    if (isCurrent) {
                        return css`
                            &&& {
                                color: ${getSystemColor("common.ui9")};
                                margin: 0;
                                ${IconWrapper}, ${TypographyStyle} {
                                    color: ${getSystemColor("common.ui9")};
                                }
                                ${TypographyStyle} {
                                    text-decoration: underline;
                                }
                            }
                        `;
                    }
                }}
            `,
        },
        megaMenuContentSection: {
            css: css`
                border-top: 1px solid ${color("common.border")};
                padding: 16px 0;
                margin: 16px 16px 0;
                display: flex;
                gap: 16px;
                &:empty,
                &:has(> div.isc-content-block:empty) {
                    display: none;
                }
                h4 {
                    text-align: left;
                }
                ${LazyImageStyle} {
                    border-radius: 8px;
                    max-width: 70px;
                }
            `,
        },
        megaMenuContentLink: {
            ...createLinkIconStyles({
                icon: ChevronRight,
                sizeVariant: "xsmall",

                linkStyles: {
                    color: "primary",
                    icon: { position: "right" },
                    css: css`
                        &&& {
                            justify-content: left;
                            padding: 0;
                            text-align: left;
                        }
                        ${IconWrapper} {
                            color: ${color("primary")};
                        }
                    `,
                },
            }),
        },
    },
} as xBaseTheme["panelMenu"];
